import { z } from 'zod'
import { ACTION_TYPES } from './action-types.ts'
import { CHOICE_FAULTS, ESCAPE_SCANS, OCCLUSION_ENDS, OFF_SWITCHES, OP_FAILURES, OP_ROLES, PHASES } from './execute/dispatch-facts.ts'
import { OP_VERBS } from './execute/errors.ts'

/** One browser operation an execution issued. `target`: what its locator matched (`execute/record.ts` `targetEvidenceOf`). */
export const ExecutedOperationSchema = z.object({ action: z.string(), result: z.string(), target: z.string().optional(), page_diff: z.string().optional() })
export type ExecutedOperation = z.infer<typeof ExecutedOperationSchema>

/** How a traced execution ended. `interrupted`: the run stopped it (`isRunAbort`), so it says nothing about the app. */
export const ExecutionOutcome = z.enum(['completed', 'failed', 'interrupted'])
export type ExecutionOutcome = z.infer<typeof ExecutionOutcome>

// execute/errors.ts `actionErrorRecord`.
const Failure = z.object({ kind: z.string(), detail: z.unknown() })

// execute/dispatch-facts.ts `DispatchRecord`.
const OpRun = { verb: z.enum(OP_VERBS), role: z.enum(OP_ROLES), atMs: z.number(), count: z.number() }
const Op = z.union([
  z.object({ ...OpRun, outcome: z.literal('notIssued') }),
  z.object({ ...OpRun, outcome: z.enum(['ok', ...OP_FAILURES]), ms: z.number(), msEff: z.number() }),
])
// recovery/index.ts `Strategy`.
const Strategy = z.union([
  z.object({ reveal: z.enum(['disclosure', 'sharedNameOpener']) }), z.object({ entry: z.literal('fill') }),
])
const NodeRef = z.object({ tag: z.string(), role: z.string().nullable(), ariaLabel: z.string().nullable(), name: z.string().nullable(), text: z.string() })
const Box = z.object({ left: z.number(), top: z.number(), right: z.number(), bottom: z.number() })
const MirrorId = z.number().nullable()
// surfaces/locate.ts `OccluderLocation`.
const Location = z.union([
  z.object({ state: z.literal('located'), surface: z.object({ id: z.string(), kind: z.string() }).nullable() }),
  z.object({ state: z.literal('timedOut'), ms: z.number() }),
  z.object({ state: z.literal('skipped'), why: z.enum(['unread', 'noLocator', 'unmirrored', 'noBudget']) }),
])
const OffSwitch = z.object({ by: z.enum(OFF_SWITCHES), node: NodeRef })
const OccluderCommon = z.object({
  hit: NodeRef, modal: z.boolean(), area: z.number(), coversViewport: z.boolean(), pinned: Box.nullable(), point: z.object({ x: z.number(), y: z.number() }), chrome: z.boolean(),
  mirror: z.object({ nodes: z.object({ anchor: MirrorId, target: MirrorId }), location: Location }).nullable(),
})
const Occluder = z.union([
  OccluderCommon.extend({ kind: z.literal('layer'), layer: NodeRef.nullable(), offSwitch: z.null() }),
  OccluderCommon.extend({ kind: z.literal('escape'), layer: NodeRef, offSwitch: OffSwitch }),
  OccluderCommon.extend({ kind: z.literal('self'), layer: z.null(), offSwitch: OffSwitch.nullable(), escapeScan: z.enum(ESCAPE_SCANS).nullable() }),
])
const ForeignError = z.object({ name: z.string(), message: z.string() })
const Choice = z.union([z.object({ click: z.string() }), z.object({ key: z.literal('Escape') }), z.object({ none: z.literal(true) })])
const Decided = z.union([
  z.object({ outcome: z.enum(['cleared', 'persisted']), input: z.boolean() }),
  z.object({ outcome: z.literal('declined') }),
  z.object({ outcome: z.literal('failed'), input: z.boolean(), failure: Failure.nullable() }),
  z.object({ outcome: z.literal('invalidChoice'), fault: z.enum(CHOICE_FAULTS) }),
]).and(z.object({ occluder: Occluder, choice: Choice, waitedMs: z.number() }))
const Decision = z.union([
  z.object({ decided: Decided }),
  z.object({ undecided: z.enum(['noChooser', 'budget']) }),
  z.object({ undecided: z.literal('chooserError'), error: ForeignError }),
])
const Occlusion = z.object({ occluder: Occluder, phase: z.enum(PHASES), atMs: z.number(), ended: z.enum(OCCLUSION_ENDS).nullable(), decision: Decision.nullable() })
const Dispatch = z.object({
  action: z.enum(ACTION_TYPES), ops: z.array(Op), probesOk: z.number(), occlusions: z.array(Occlusion),
  remedy: z.object({ strategy: Strategy, atMs: z.number(), after: Failure }).nullable(),
  outcome: z.union([
    z.object({ performed: z.enum(['performed', 'skippedPrivateFilled']) }),
    z.object({ failed: Failure, dispatched: z.boolean() }),
    z.object({ stopped: ForeignError }),
  ]),
  ms: z.number(),
})

/** `journey.execute-result` data: `result` describes a returned execution, `error` why it failed or stopped, `failure`
 * the executor's typed failure, `dispatch` its dispatch's record. */
export const ExecuteResultData = z.object({
  outcome: ExecutionOutcome, result: z.string().optional(), error: z.string().optional(), failure: Failure.optional(), dispatch: Dispatch.optional(),
  operations: z.array(ExecutedOperationSchema).default([]),
})
export type ExecuteResultData = z.infer<typeof ExecuteResultData>

// Records written before `outcome` existed never marked a run stop. The dispatch record is not read back: a stored
// one may name a verb or strategy since renamed.
const RecordedExecuteResult = ExecuteResultData.omit({ dispatch: true }).extend({ outcome: ExecutionOutcome.optional() })
export type RecordedExecuteResult = Omit<ExecuteResultData, 'dispatch'>

/** Parses a recorded `journey.execute-result`, without its dispatch record, with its action's `error`. */
export function parseExecuteResult(data: unknown, error: string | null): RecordedExecuteResult {
  const recorded = RecordedExecuteResult.parse(data)
  return { ...recorded, outcome: recorded.outcome ?? (recorded.error || error ? 'failed' : 'completed') }
}
